import { useCallback } from 'react';
import {
    DicomInstancesRequestBody,
    useDicomInstances,
} from '~/backend/connector-next-api/connector-next-components';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';

/**
 * Uploads the dose the viewer holds to the connector before a task reads it.
 *
 * A predicted dose lives in the browser and in the task's result store, not in
 * the patient record, so a task that names it by UID needs it imported first.
 * The DICOM ingest route re-applies a dose header on every import, so
 * re-sending the same SOP Instance UID replaces the stored copy. Structure
 * sets, image slices, and registrations do not behave this way: the same route
 * ignores a re-sent one to protect its approval, so they are saved instead.
 *
 * @returns A function to run before task submission.
 * @throws If the upload fails. The caller must abort the submission.
 */
export default function useUploadDoseForTask() {
    const currentDoseMatrix = useCurrentDose();
    const { mutateAsync: importInstances } = useDicomInstances();

    return useCallback(async (): Promise<void> => {
        if (!currentDoseMatrix) {
            return;
        }

        const formData = new FormData();
        formData.append(
            'files',
            currentDoseMatrix.dicomDose.getAsDicomBlob(),
            `RD_${currentDoseMatrix.dicomUid}.dcm`
        );

        await importInstances({
            body: formData as unknown as DicomInstancesRequestBody,
            headers: { 'Content-Type': 'multipart/form-data' },
            // Safe to retry although POST: a re-sent dose replaces its
            // stored copy with the same bytes, and the response is not read.
            retryPolicy: 'transient',
        });
    }, [currentDoseMatrix, importInstances]);
}
